import { useContext, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { yupResolver } from "@hookform/resolvers/yup"
import { object, string } from "yup"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import {
  ACCOUNT_HOLDER_NAME_MAX_LENGTH,
  ACCOUNT_NUMBER_MAX_LENGTH,
  ROUTING_NUMBER_LENGTH,
  TOKEN_TYPE,
} from "../../../../ach/constants"
import { useCreateStripeAch } from "./useCreateStripeAch"
import {
  CountryIsoCode,
  CurrencyEnum,
  PaymentMethodsCustomerBankAccountEnumType,
} from "@oysterhr/schema"
import { useConfiguredToasts, useReusableWizard } from "@oysterhr/feature-components"
import { useParams } from "react-router-dom"
import { Token } from "@stripe/stripe-js"
import { useStripe } from "@stripe/react-stripe-js"
import { BankDetailsContext } from "../contexts/BankDetailsContext"
import { captureMessage } from "@sentry/react"
import { useUpdateCustomerBankAccount } from "../../../../hooks/useUpdateCustomerBankAccount"
import { useCreateCustomerBankAccount } from "../../../../hooks/useCreateCustomerBankAccount"
import { useNewCompanyPaymentMethods } from "../../../../hooks/useNewCompanyPaymentMethods"
import { PaymentMethodCategoryEnumType } from "../../../../enums"
import { AddAchFormValues } from "~/app/pages/payments/types"

const defaultBankDetails = {
  country: "US",
  currencyCode: "USD",
  routingNumber: "",
  accountNumber: "",
  accountHolderName: "",
}

const useUpsertAchPaymentMethodForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const stripe = useStripe()
  const { goToNext } = useReusableWizard()
  const { paymentMethodType } = useParams()
  const { accountAspect: companyId } = useAccountAspect()
  const { bankDetails, setBankDetails } = useContext(BankDetailsContext)
  const { t } = useTranslation("company_interface/AddStripeAchPage")
  const { displayErrorToast } = useConfiguredToasts()

  const [createAchPaymentMethod, { loading: creatingAchPaymentMethod }] = useCreateStripeAch({
    analyticsEventName: "stripe_ach_payment_method_created",
  })
  const [updateCustomerBankAccount, { loading: updatingCustomerBankAccount }] =
    useUpdateCustomerBankAccount()
  const [createCustomerBankAccount, { loading: creatingCustomerBankAccount }] =
    useCreateCustomerBankAccount()
  const { data: companyPaymentMethodsData } = useNewCompanyPaymentMethods({
    variables: { companyId },
    fetchPolicy: "cache-and-network",
  })
  const customerBankAccount = companyPaymentMethodsData?.company.customerBankAccount

  const validationSchema = useMemo(
    () =>
      object().shape({
        country: string(),
        currencyCode: string(),
        routingNumber: string()
          .trim()
          .required(t("required.routingNumber"))
          .matches(/^\d+$/, t("validation.number"))
          .min(ROUTING_NUMBER_LENGTH, t("validation.digits", { digits: ROUTING_NUMBER_LENGTH }))
          .max(ROUTING_NUMBER_LENGTH, t("validation.digits", { digits: ROUTING_NUMBER_LENGTH })),
        accountNumber: string()
          .trim()
          .required(t("required.accountNumber"))
          .max(
            ACCOUNT_NUMBER_MAX_LENGTH,
            t("validation.max", { characters: ACCOUNT_NUMBER_MAX_LENGTH }),
          ),
        accountHolderName: string()
          .trim()
          .required(t("required.accountHolderName"))
          .max(
            ACCOUNT_HOLDER_NAME_MAX_LENGTH,
            t("validation.digits", { digits: ACCOUNT_HOLDER_NAME_MAX_LENGTH }),
          ),
      }),
    [t],
  )
  const form = useForm({
    defaultValues: bankDetails || defaultBankDetails,
    resolver: yupResolver(validationSchema),
  })

  const createFTEPaymentMethod = (values: AddAchFormValues) => {
    return createCustomerBankAccount({
      variables: {
        ownerId: companyId,
        paymentMethodType: PaymentMethodsCustomerBankAccountEnumType.Ach,
        billingAddress: null,
        userNotSigner: null,
        bankDetails: {
          ach: {
            accountHolderName: values.accountHolderName,
            currencyCode: values.currencyCode as CurrencyEnum,
            countryCode: values.country as CountryIsoCode,
            accountNumber: values.accountNumber,
            achRoutingNumber: values.routingNumber,
          },
        },
      },
      onCompleted: data => {
        if (data.createCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data?.createCustomerBankAccount?.errors.join(", "),
          })
        } else {
          onSuccess()
        }
      },
    })
  }

  const updateFTEPaymentMethod = async (
    customerBankAccountId: string,
    values: AddAchFormValues,
  ) => {
    return await updateCustomerBankAccount({
      variables: {
        customerBankAccountId,
        userNotSigner: null,
        billingAddress: null,
        bankDetails: {
          ach: {
            accountHolderName: values.accountHolderName,
            currencyCode: values.currencyCode as CurrencyEnum,
            countryCode: values.country as CountryIsoCode,
            accountNumber: values.accountNumber,
            achRoutingNumber: values.routingNumber,
          },
        },
      },
      onCompleted: data => {
        if (data.updateCustomerBankAccount.errors.length) {
          displayErrorToast({
            content: data?.updateCustomerBankAccount?.errors.join(", "),
          })
        } else {
          onSuccess()
        }
      },
    })
  }

  const getToken = async (values: AddAchFormValues): Promise<Token> => {
    if (!stripe) {
      throw new Error("Stripe isn't ready")
    }

    const { token, error } = await stripe.createToken(TOKEN_TYPE, {
      country: values.country,
      currency: values.currencyCode,
      routing_number: values.routingNumber,
      account_number: values.accountNumber,
      account_holder_name: values.accountHolderName,
      account_holder_type: "company",
    })

    if (error) {
      captureMessage(error.message || "Couldn't submit ACH direct debit details", {
        extra: {
          ...error,
        },
      })
      throw new Error(error.message)
    }

    return token
  }

  const createPaymentMethod = async (values: AddAchFormValues) => {
    try {
      const token = await getToken(values)

      return await createAchPaymentMethod({
        variables: {
          ownerId: companyId,
          accountNumber: values.accountNumber,
          routingNumber: values.routingNumber,
          accountHolderName: values.accountHolderName,
          stripeBankToken: token.id,
        },
        onCompleted: data => {
          if (data.createPaymentMethodStripeAch.errors.length) {
            throw new Error(data.createPaymentMethodStripeAch.errors.join(", "))
          } else {
            onSuccess()
          }
        },
      })
    } catch (err) {
      displayErrorToast({
        content: (err as Error).message,
      })
      console.error(err)
      throw err
    }
  }

  const onSubmit = async (values: AddAchFormValues) => {
    if (paymentMethodType === PaymentMethodCategoryEnumType.Fte) {
      return customerBankAccount
        ? updateFTEPaymentMethod(customerBankAccount.id, values)
        : createFTEPaymentMethod(values)
    } else {
      // NOTE: We delay creation of contractor payment method until last step
      // so we don't need update here.
      return createPaymentMethod(values)
    }
  }

  const onNext = (values: AddAchFormValues) => {
    setBankDetails(values)
    goToNext()
  }

  return {
    ...form,
    onSubmit,
    onNext,
    formIsReady: !!stripe,
    loading: creatingCustomerBankAccount || updatingCustomerBankAccount || creatingAchPaymentMethod,
  }
}

export default useUpsertAchPaymentMethodForm
